import { notification } from 'antd'
import { FC, ReactNode, useEffect, useState, useRef, useCallback } from 'react'
import { FormProvider, UseFormReturn } from 'react-hook-form'
import { useToggle } from 'react-use'
import SuccessContentComponent from './SuccessContent'
import { Wrapper } from './styled'
import Header, { HeaderProps } from './Header'
import Footer, { FooterProps } from './Footer'

export enum FromButtons {
  MAIN,
  ADD_MORE,
  NEXT,
}

type Props = {
  children?: ReactNode
  successContentAddTitle?: string
  successContentEditTitle?: string
  successContentDescription?: ReactNode
  methods: UseFormReturn
  isSuccess?: boolean
  isError?: boolean
  reset?: () => void
  withNotificationBanner?: boolean
  CustomErrorComponent?: FC<any & { onClose: () => void }> // correct
  error?: any // correct
  withStopPropagation?: boolean
  withoutReset?: boolean
  withoutAddMore?: boolean
  minHeightVh?: number
  className?: string
  CustomFooter?: FC<FooterProps>
  data?: any // correct
  onSuccessVisibleChange?: (visible: boolean) => void
} & HeaderProps &
  Omit<FooterProps, 'fromButton' | 'setFromButton'>

const AddNewItemModalLayout: FC<Props> = ({
  isEdit = false,
  titlePostfix,
  additionalTitleContent,
  onSubmitForm,
  methods,
  isLoading,
  onSaveAndAddMoreText,
  customAddText,
  successContentAddTitle,
  handleCloseModal,
  successContentEditTitle,
  successContentDescription,
  isError,
  isSuccess,
  withSaveAndAddMore = false,
  withoutAddMore = false,
  onSaveAndAddMore,
  withNotificationBanner = false,
  CustomErrorComponent,
  error: errorProps,
  withStopPropagation = false,
  withoutReset = false,
  saveText,
  minHeightVh = 40,
  className,
  customFullTitle,
  CustomFooter,
  data,
  onSuccessVisibleChange,
  children,
}) => {
  const successAddText = successContentAddTitle || 'Добавление прошло успешно'
  const successEditText = successContentEditTitle || 'Изменение прошло успешно'
  const { reset } = methods

  const [error, toggleError] = useToggle(false)
  const [success, toggleSuccess] = useToggle(false)
  const [fromButton, setFromButton] = useState<FromButtons>(FromButtons.MAIN)
  const fromButtonRef = useRef<FromButtons>(FromButtons.MAIN)
  const successHandledRef = useRef(false)

  const setSuccessVisible = useCallback(
    (visible: boolean) => {
      toggleSuccess(visible)
      onSuccessVisibleChange?.(visible)
    },
    [onSuccessVisibleChange, toggleSuccess]
  )

  const handleSetFromButton: React.Dispatch<React.SetStateAction<FromButtons>> = (value) => {
    const newValue = typeof value === 'function' ? value(fromButtonRef.current) : value
    fromButtonRef.current = newValue
    setFromButton(newValue)
  }

  const handleAddMore = () => {
    setSuccessVisible(false)
  }

  useEffect(() => {
    if (!isSuccess) {
      successHandledRef.current = false
      return
    }

    if (successHandledRef.current) {
      return
    }

    successHandledRef.current = true

    // Use ref to get the actual button that was clicked (avoids race condition with state updates)
    const wasFromMain = fromButtonRef.current === FromButtons.MAIN

    if (!withoutReset) {
      reset()
    }

    if (!isEdit && !withNotificationBanner && wasFromMain) {
      setSuccessVisible(true)
    } else {
      notification.success({
        message: isEdit ? successEditText : successAddText,
      })

      if (wasFromMain) {
        handleCloseModal?.()
      }
    }

    // Reset ref to MAIN for next submission
    fromButtonRef.current = FromButtons.MAIN
  }, [
    handleCloseModal,
    isEdit,
    isSuccess,
    reset,
    setSuccessVisible,
    successAddText,
    successEditText,
    withNotificationBanner,
    withoutReset,
  ])

  useEffect(() => {
    if (isError) {
      toggleError(true)
    }
  }, [isError, toggleError])

  if (CustomErrorComponent && error) {
    return <CustomErrorComponent error={errorProps} onClose={toggleError} />
  }

  if (handleCloseModal && success) {
    return (
      <SuccessContentComponent
        isEdit={isEdit}
        onAddMore={handleAddMore}
        onClose={handleCloseModal}
        addTitle={successAddText}
        editTitle={successEditText}
        description={successContentDescription || ''}
        withoutAddMore={withoutAddMore}
      />
    )
  }

  const FooterComponent = CustomFooter || Footer

  return (
    <FormProvider {...methods}>
      <Wrapper
        className={className}
        minHeightVh={minHeightVh}
        onClick={withStopPropagation ? (e) => e.stopPropagation() : undefined}
      >
        <div>
          {(customFullTitle || titlePostfix) && (
            <Header
              customFullTitle={customFullTitle}
              additionalTitleContent={additionalTitleContent}
              customAddText={customAddText}
              isEdit={isEdit}
              titlePostfix={titlePostfix}
            />
          )}
          {children}
        </div>
        <FooterComponent
          isEdit={isEdit}
          onSubmitForm={onSubmitForm}
          isLoading={isLoading}
          onSaveAndAddMore={onSaveAndAddMore}
          onSaveAndAddMoreText={onSaveAndAddMoreText}
          withSaveAndAddMore={withSaveAndAddMore}
          saveText={saveText}
          fromButton={fromButton}
          setFromButton={handleSetFromButton}
          handleCloseModal={handleCloseModal}
          data={data}
        />
      </Wrapper>
    </FormProvider>
  )
}

export type { Props as AddNewItemModalLayoutProps }
export default AddNewItemModalLayout
